import { useCallback, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAtom, useSetAtom } from 'jotai'
import { userAtom } from '../../state/auth'
import { jobOpportunitiesAtom } from '../../state/jobOpportunities'
import { jobOpportunitiesApi, JobOpportunity } from '../../services/api'
import { useWebSocket } from '../../hooks/useWebSocket'
import { useNotification } from '../../hooks/useNotification'
import { usePortalData } from './PortalDataProvider'
import { isSignalJob } from '../job-applications/jobTabRouting'
import { interviewGuidePublishedMessage, portalInterviewPath } from '../../utils/portalInterviewLink'

/**
 * MWA-220 — Global portal-side WebSocket layer.
 *
 * Mounted once in PortalLayout so the client's job-opportunity data stays
 * fresh app-wide regardless of which portal page they're on. It:
 *  - On mount (when userAtom.clientId is set), loads the client's jobs into
 *    jobOpportunitiesAtom so the nav badge, dashboard "Awaiting your decision"
 *    card, and the kanban board all share one hydrated source of truth.
 *  - Keeps jobOpportunitiesAtom in sync via WS broadcasts, and fires a toast
 *    when a staff member adds an application that awaits the client's decision.
 *
 * Renders null — purely a side-effect / listener component.
 */
export default function GlobalPortalNotifications() {
  const [user] = useAtom(userAtom)
  const { refresh, visibility } = usePortalData()
  const navigate = useNavigate()
  const setJobs = useSetAtom(jobOpportunitiesAtom)
  const { showInfo } = useNotification()

  const clientId = user?.clientId || ''

  // Load the client's jobs once we know which client this is, so the rest of
  // the portal (badge, dashboard card, board) shares fresh data.
  useEffect(() => {
    if (!clientId) return
    let cancelled = false
    ;(async () => {
      try {
        const result = await jobOpportunitiesApi.list({ clientId })
        if (!cancelled) setJobs(Array.isArray(result) ? result : [])
      } catch (err) {
        console.error('Failed to load job applications:', err)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [clientId, setJobs])

  // MWA-390 — the same read, for catching up after the socket has been down.
  const reloadJobs = useCallback(async () => {
    if (!clientId) return
    try {
      const result = await jobOpportunitiesApi.list({ clientId })
      setJobs(Array.isArray(result) ? result : [])
    } catch (err) {
      console.error('Failed to reload job applications:', err)
    }
  }, [clientId, setJobs])

  const handlers = useMemo(
    () => ({
      ws_reconnected: () => {
        refresh()
        void reloadJobs()
      },
      // Staff advanced this client's stage, which can change what the portal
      // shows. Silent on purpose: "your stage changed" is internal pipeline
      // vocabulary, and the client sees the result rather than the mechanism.
      client_stage_changed: () => {
        refresh()
      },
      job_opportunity_created: (message: { data: unknown }) => {
        const newJob = message.data as JobOpportunity
        setJobs((prev) => {
          if (prev.some((j) => j.id === newJob.id)) return prev
          return [...prev, newJob]
        })
      },
      job_opportunity_updated: (message: { data: unknown }) => {
        const updated = message.data as JobOpportunity
        setJobs((prev) => prev.map((j) => (j.id === updated.id ? updated : j)))
      },
      job_opportunity_deleted: (message: { data: unknown }) => {
        const data = message.data as { id: string }
        setJobs((prev) => prev.filter((j) => j.id !== data.id))
      },
      job_application_awaiting_decision: (message: { data: unknown }) => {
        const job = message.data as JobOpportunity
        setJobs((prev) => {
          if (prev.some((j) => j.id === job.id)) {
            return prev.map((j) => (j.id === job.id ? job : j))
          }
          return [...prev, job]
        })
        showInfo('A new job application was added — choose how you\'d like to proceed.')
      },
      // MWA-240 — a staff member added an opportunity that needs no client
      // decision (e.g. logged directly as Applied). Sync it in and toast so the
      // client knows their Job Search list grew. (Decision-flow adds use
      // job_application_awaiting_decision above; client self-adds are silent.)
      // NOTE: copy is a sensible default — final wording is pending Dee's team.
      job_opportunity_added: (message: { data: unknown }) => {
        const newJob = message.data as JobOpportunity
        setJobs((prev) => {
          if (prev.some((j) => j.id === newJob.id)) return prev
          return [...prev, newJob]
        })
        // MWA-421 — a signal logged by staff arrives on this same event, and
        // its row is on the Signals page, not the Front Door.
        showInfo(
          isSignalJob(newJob)
            ? 'A new signal was added to your Signals page.'
            : 'A new opportunity was added to your Front Door page.'
        )
      },
      // MWA-394 — staff published a prep guide. The interview opens as a dialog
      // over the Front Door page, so the toast is only worth showing while that
      // page is available at this client's stage; otherwise it would point at
      // the "unavailable" page.
      // NOTE: copy is a sensible default — final wording is pending Dee's team.
      interview_guide_published: (message: { data: unknown }) => {
        const data = (message.data || {}) as { interviewId?: string; company?: string; role?: string }
        const interviewId = data.interviewId
        if (!interviewId) return
        if (!visibility.isPathVisible('/portal/opportunities')) return
        showInfo(interviewGuidePublishedMessage(data), {
          label: 'Open',
          onClick: () => navigate(portalInterviewPath(interviewId, 'guide')),
        })
      },
      // MWA-240 — staff added a single target company to the client's list.
      // Toast only (target companies aren't in jobOpportunitiesAtom; the Target
      // Companies tab refetches on open). Copy pending Dee's team.
      target_company_added: (message: { data: unknown }) => {
        const company = (message.data as { companyName?: string }) || {}
        showInfo(
          company.companyName
            ? `A new target company was added to your list: ${company.companyName}.`
            : 'A new target company was added to your list.'
        )
      },
    }),
    [setJobs, showInfo, refresh, reloadJobs, navigate, visibility]
  )

  // Only open the connection for portal clients.
  useWebSocket(clientId ? handlers : {})

  return null
}
